<!--
  MsaConfirmPanel -- the Owner's confirm at the foot of /legal/agreement.

  ONE agreement (Jake, 2026-10-20): the Master Services Agreement. This is a
  TEMPORARY affordance until the chosen e-signing platform lands; the seam
  is the backend service function the POST calls
  (record_msa_acceptance in api/src/services/agreement_acceptance.py), which
  the e-sign callback will call instead. The confirm writes ONE record (the
  acceptance row AND distributors.msa_*), so the dashboard, Compliance and
  the store launch gate all flip from it.

  /legal/agreement is PUBLIC, so the router guard never hydrates a session
  here; this panel asks the server who is signed in (/auth/me) and lets the
  server decide what they may do:

    signed out / anonymous storefront   sign-in prompt, no form
    signed in, not the Owner            note: an Owner must confirm
    Owner, current version unconfirmed  form: typed name, title, the
                                        authority attestation, Confirm
    confirmed                           who, title, when, version

  Native inputs (not Vuetify): ui-runner clicks a bare [data-testid], and a
  native <label for> is the reliable click target for the checkbox.

  data-testids:
    legal-agreement-confirm              panel root
    legal-agreement-signin(-link)        signed-out prompt + its link
    legal-agreement-owner-note           not the Owner
    legal-agreement-confirm-form         the form
    legal-agreement-new-version          an older version is on file
    legal-agreement-signer-name / -signer-title
    legal-agreement-attest / -attest-toggle   checkbox / its <label for>
    legal-agreement-confirm-button
    legal-agreement-confirm-error
    legal-agreement-confirmed            the confirmed record
    legal-agreement-confirm-load-error   status could not be read
-->
<template>
  <section class="msa-confirm" data-testid="legal-agreement-confirm" aria-labelledby="msa-confirm-heading">
    <h2 id="msa-confirm-heading" class="msa-confirm__title">Confirm this agreement</h2>

    <p v-if="state === 'loading'" class="msa-confirm__muted">Checking your account...</p>

    <p v-else-if="state === 'signedOut'" data-testid="legal-agreement-signin">
      You are not signed in. An account Owner confirms this agreement for
      their company, and the confirm form appears once an Owner is signed in.
      <a :href="signInHref" data-testid="legal-agreement-signin-link">Sign in to confirm</a>.
    </p>

    <p v-else-if="state === 'notOwner'" class="msa-confirm__muted" data-testid="legal-agreement-owner-note">
      An account Owner must confirm this agreement for your company. You can
      read it here; ask your Owner to sign in and confirm it.
    </p>

    <p v-else-if="state === 'loadError'" class="msa-confirm__error" data-testid="legal-agreement-confirm-load-error">
      We could not check your agreement status. Reload the page to try again.
    </p>

    <p v-else-if="state === 'confirmed'" class="msa-confirm__done" data-testid="legal-agreement-confirmed">
      Confirmed {{ confirmedOn }} by {{ acceptance.signer_name }},
      {{ acceptance.signer_title }} (version {{ acceptance.agreement_version }}).
    </p>

    <form
      v-else-if="state === 'form'"
      class="msa-confirm__form"
      data-testid="legal-agreement-confirm-form"
      novalidate
      @submit.prevent="confirm"
    >
      <p v-if="olderVersion" class="msa-confirm__muted" data-testid="legal-agreement-new-version">
        Your company confirmed version {{ olderVersion }}. This is version
        {{ status.current_version }}; please review and confirm it.
      </p>
      <label class="msa-confirm__field">
        <span>Your full name</span>
        <input
          v-model="signerName"
          type="text"
          autocomplete="name"
          :maxlength="NAME_MAX"
          data-testid="legal-agreement-signer-name"
        />
      </label>
      <label class="msa-confirm__field">
        <span>Your title</span>
        <input
          v-model="signerTitle"
          type="text"
          autocomplete="organization-title"
          placeholder="For example, Owner or President"
          :maxlength="TITLE_MAX"
          data-testid="legal-agreement-signer-title"
        />
      </label>
      <div class="msa-confirm__attest">
        <input
          id="legal-agreement-attest-checkbox"
          v-model="attested"
          type="checkbox"
          data-testid="legal-agreement-attest"
        />
        <label
          for="legal-agreement-attest-checkbox"
          data-testid="legal-agreement-attest-toggle"
          :data-checked="attested ? 'true' : 'false'"
        >I am authorized to bind my company to this agreement</label>
      </div>
      <p v-if="error" class="msa-confirm__error" role="alert" data-testid="legal-agreement-confirm-error">
        {{ error }}
      </p>
      <button
        type="button"
        class="msa-confirm__button"
        data-testid="legal-agreement-confirm-button"
        :disabled="submitting"
        @click="confirm"
      >
        Confirm agreement
      </button>
    </form>
  </section>
</template>

<script>
import {
  confirmAgreement,
  getAgreementStatus,
  getSession,
  storedSessionToken,
} from "@/services/agreementService";

// Mirror SIGNER_NAME_MAX / SIGNER_TITLE_MAX in
// api/src/services/agreement_acceptance.py (the server re-checks).
const NAME_MAX = 255;
const TITLE_MAX = 120;

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

export default {
  name: "MsaConfirmPanel",
  data() {
    return {
      // loading | signedOut | notOwner | loadError | form | confirmed
      state: "loading",
      distributorId: null,
      status: null,
      signerName: "",
      signerTitle: "",
      attested: false,
      error: "",
      submitting: false,
      NAME_MAX,
      TITLE_MAX,
    };
  },
  computed: {
    signInHref() {
      return `/login?next=${encodeURIComponent("/legal/agreement")}`;
    },
    acceptance() {
      return (this.status && this.status.acceptance) || {};
    },
    confirmedOn() {
      return formatDate(this.acceptance.accepted_at);
    },
    // A REAL earlier version on file (never the backfill, which has none).
    olderVersion() {
      const s = this.status || {};
      return s.signed && !s.confirmed && s.msa_version ? s.msa_version : "";
    },
  },
  async mounted() {
    if (!storedSessionToken()) {
      this.state = "signedOut";
      return;
    }
    let session;
    try {
      session = (await getSession()).data || {};
    } catch (e) {
      const code = e && e.response && e.response.status;
      this.state = code === 401 ? "signedOut" : "loadError";
      return;
    }
    if (session.session_is_anonymous_storefront === true) {
      this.state = "signedOut";
      return;
    }
    const id = session.distributor && session.distributor.id;
    if (!id) {
      this.state = "notOwner";
      return;
    }
    this.distributorId = id;
    try {
      this.apply((await getAgreementStatus(id)).data);
    } catch (e) {
      const code = e && e.response && e.response.status;
      this.state = code === 403 ? "notOwner" : code === 401 ? "signedOut" : "loadError";
    }
  },
  methods: {
    apply(status) {
      this.status = status || {};
      if (this.status.confirmed) {
        this.state = "confirmed";
      } else if (this.status.can_confirm) {
        this.state = "form";
      } else {
        this.state = "notOwner";
      }
    },
    problem() {
      if (!this.signerName.trim()) return "Enter your full name.";
      if (!this.signerTitle.trim()) {
        return "Enter your title (for example, Owner or President).";
      }
      if (!this.attested) {
        return "Confirm that you are authorized to bind your company to this agreement.";
      }
      return "";
    },
    async confirm() {
      if (this.submitting) return;
      const problem = this.problem();
      if (problem) {
        this.error = problem;
        return;
      }
      this.error = "";
      this.submitting = true;
      try {
        const resp = await confirmAgreement(this.distributorId, {
          agreement_version: this.status.current_version,
          signer_name: this.signerName.trim().split(/\s+/).join(" "),
          signer_title: this.signerTitle.trim().split(/\s+/).join(" "),
          authority_attested: true,
        });
        this.apply(resp && resp.data);
      } catch (e) {
        this.error =
          (e && e.response && e.response.data && e.response.data.error) ||
          "We could not record your confirmation. Please try again.";
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.msa-confirm {
  margin-top: 40px;
  padding: 20px;
  border: 1px solid #c9ced6;
  border-radius: 6px;
  background: #f8fafc;
}
.msa-confirm__title {
  font-size: 1.15rem;
  margin: 0 0 12px;
}
.msa-confirm__muted {
  color: #4b5563;
}
.msa-confirm__error {
  color: #b91c1c;
}
.msa-confirm__done {
  color: #166534;
  font-weight: 600;
}
.msa-confirm__form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 480px;
}
.msa-confirm__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.msa-confirm__field input {
  padding: 8px 10px;
  border: 1px solid #9ca3af;
  border-radius: 4px;
  font: inherit;
}
.msa-confirm__attest {
  display: flex;
  align-items: center;
  gap: 8px;
}
.msa-confirm__attest label {
  cursor: pointer;
}
.msa-confirm__button {
  align-self: flex-start;
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  background: #1d4ed8;
  color: #ffffff;
  font: inherit;
  cursor: pointer;
}
.msa-confirm__button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
@media print {
  .msa-confirm {
    display: none;
  }
}
</style>
